import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import StagingBanner from './StagingBanner.jsx';
import Toaster from './Toast.jsx';
import { useAuth } from '../useAuth.js';
import { useDraftActions } from '../hooks/useSend.js';

const NAV = [
  { to: '/',           label: 'Dashboard' },
  { to: '/sends',      label: 'Sends' },
  { to: '/approvals',  label: 'Approvals' },
  { to: '/contacts',   label: 'Contacts' },
  { to: '/audiences',  label: 'Audiences' },
  { to: '/settings',   label: 'Settings' },
  { to: '/guide',      label: 'Guide' },
];

const SWITCHER_APPS = [
  { id: 'phone',        label: 'Phone',         href: 'https://phone.trovex.com' },
  { id: 'intelligence', label: 'Intelligence',  href: 'https://intelligence.trovex.com' },
  { id: 'inbox',        label: 'Inbox',         href: 'https://inbox.trovex.com' },
  { id: 'media',        label: 'Media',         href: 'https://media.trovex.com' },
  { id: 'events',       label: 'Events',        href: 'https://events.trovex.com' },
  { id: 'projects',     label: 'Projects',      href: 'https://projects.trovex.com' },
  { id: 'systems',      label: 'Systems',       href: 'https://systems.trovex.com' },
];

function initials(name, email) {
  const src = (name || email || '').trim();
  if (!src) return '·';
  if (src.includes('@')) {
    const local = src.split('@')[0];
    return local.split(/[._-]/).filter(Boolean).slice(0, 2).map(s => s[0].toUpperCase()).join('') || src[0].toUpperCase();
  }
  return src.split(/\s+/).slice(0, 2).map(s => s[0].toUpperCase()).join('');
}

export default function Layout() {
  const { displayName, email, signOut } = useAuth();
  const { createDraft } = useDraftActions();
  const navigate = useNavigate();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const switcherRef = useRef(null);
  const userMenuRef = useRef(null);

  useEffect(() => {
    function onDown(e) {
      if (switcherRef.current && !switcherRef.current.contains(e.target)) setSwitcherOpen(false);
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  async function newDraft() {
    const id = await createDraft({ templateId: null, name: 'Untitled broadcast' });
    navigate(`/sends/${id}`);
  }

  return (
    <div className="min-h-screen flex flex-col gradient-mesh">
      <StagingBanner />

      <nav className="sticky top-0 z-50">
        <div className="max-w-screen-2xl mx-auto px-2 md:px-4 lg:px-6 pt-3">
          <div className="grid grid-cols-[1fr_auto_1fr] items-center h-14 px-3 md:px-4 bg-white/60 dark:bg-gray-900/60 backdrop-blur-xl rounded-2xl shadow-sm border border-gray-200/40 dark:border-white/5">
            <div className="relative justify-self-start" ref={switcherRef}>
              <button
                type="button"
                onClick={() => setSwitcherOpen((o) => !o)}
                className="flex items-center gap-2 px-2 md:px-3 py-1.5 rounded-full hover:bg-white/60 dark:hover:bg-white/5 transition-all"
                aria-haspopup="menu"
                aria-expanded={switcherOpen}
              >
                <div className="w-7 h-7 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-md shadow-teal-500/20">
                  <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l9 6 9-6M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 hidden sm:block">Broadcast</span>
                <svg className={`w-4 h-4 text-gray-400 transition-transform hidden sm:block ${switcherOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {switcherOpen && (
                <div role="menu" className="absolute left-0 top-full mt-2 w-56 menu-panel border border-gray-200 dark:border-gray-700 py-1 z-50 animate-fade-in-up">
                  <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
                    <p className="text-xs font-medium text-gray-400 dark:text-gray-500">Switch app</p>
                  </div>
                  {SWITCHER_APPS.map((a) => (
                    <a
                      key={a.id}
                      href={a.href}
                      className="flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7-7 7M5 12h16" />
                      </svg>
                      {a.label}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="hidden md:flex items-center gap-0.5">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `px-3 py-1.5 rounded-full text-sm transition-all ${
                      isActive
                        ? 'bg-primary-500/10 text-primary-600 dark:bg-primary-500/15 dark:text-primary-300 font-semibold'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-white/60 dark:hover:bg-white/5'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </div>

            <div className="justify-self-end flex items-center gap-2">
              <button
                type="button"
                onClick={newDraft}
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-sm font-semibold shadow-md shadow-teal-500/20 hover:shadow-teal-500/30 transition-all"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                New broadcast
              </button>
              <div className="relative" ref={userMenuRef}>
                <button
                  type="button"
                  onClick={() => setUserMenuOpen((o) => !o)}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-full hover:bg-white/60 dark:hover:bg-white/5 transition-all"
                >
                  <div className="w-7 h-7 rounded-full bg-primary-500/20 flex items-center justify-center text-xs text-primary-600 dark:text-primary-300 font-medium">
                    {initials(displayName, email)}
                  </div>
                </button>
                {userMenuOpen && (
                  <div role="menu" className="absolute right-0 top-full mt-2 w-60 menu-panel border border-gray-200 dark:border-gray-700 py-1 z-50 animate-fade-in-up">
                    <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{displayName || 'Trovex user'}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{email}</p>
                    </div>
                    <button
                      onClick={signOut}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile nav pills */}
          <div className="md:hidden flex items-center gap-1 mt-2 px-1 overflow-x-auto">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `whitespace-nowrap px-3 py-1.5 rounded-full text-sm ${
                    isActive
                      ? 'bg-primary-500/10 text-primary-600 dark:text-primary-300 font-semibold'
                      : 'text-gray-600 dark:text-gray-300'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      </nav>

      <main className="flex-1 max-w-screen-2xl w-full mx-auto px-3 md:px-5 lg:px-7 py-8">
        <Outlet />
      </main>

      <Toaster />
    </div>
  );
}
